import { SvgAnnotations } from '@/components/svg-annotations';
import { LAYER_PRINT_ATTRIBUTE } from '@/svgcanvas/layer-tools';
import { svgEditServiceInstance } from './svg-edit-service';

class LayersInterop {
    private activeDragToggle: { mode: string; targetState: boolean; appliedKeys: Set<string> } | null = null;

    private get canvas() {
        return svgEditServiceInstance.canvas;
    }

    private notifyChanged(element?: SVGGraphicsElement) {
        svgEditServiceInstance.ElementStateHasChanged(element);
        SvgAnnotations.UpdateAnnotations();

        // Send the layer change to Blazor now, rather than after the idle delay. This used to dispatch
        // svgcontent-changed, which now means a whole new document was loaded, and would make Blazor re-read it all.
        window["_zaa_services"]?.documentSyncService?.sendChanges();
    }

    public getCurrentLayerName(): string | null {
        return this.canvas?.getCurrentDrawing().getCurrentLayerName() ?? null;
    }

    public createLayer(name: string): boolean {
        if (!this.canvas) return false;
        this.canvas.createLayer(name);
        this.notifyChanged();
        return true;
    }

    public renameCurrentLayer(name: string): boolean {
        if (!this.canvas) return false;
        this.canvas.renameCurrentLayer(name);
        this.notifyChanged();
        return true;
    }

    public duplicateCurrentLayer(name: string): boolean {
        if (!this.canvas) return false;
        this.canvas.cloneLayer(name);
        this.notifyChanged();
        return true;
    }

    public deleteCurrentLayer(): boolean {
        if (!this.canvas || this.canvas.getCurrentDrawing().getNumLayers() <= 1) return false;
        this.canvas.deleteCurrentLayer();
        this.notifyChanged();
        return true;
    }

    public mergeCurrentLayerWithBelow(): boolean {
        if (!this.canvas) return false;
        this.canvas.mergeLayerWithBelow();
        this.notifyChanged();
        return true;
    }

    public mergeAllLayers(): boolean {
        if (!this.canvas) return false;
        this.canvas.mergeAllLayers();
        this.notifyChanged();
        return true;
    }

    public selectLayer(name: string): boolean {
        if (!this.canvas) return false;
        const changed = this.canvas.setCurrentLayer(name);
        if (changed) svgEditServiceInstance.ElementStateHasChanged();
        return changed;
    }

    public moveLayer(name: string, change: number): boolean {
        if (!this.canvas) return false;
        const drawing = this.canvas.getCurrentDrawing();
        const previousLayer = drawing.getCurrentLayerName();
        if (!this.canvas.setCurrentLayer(name)) return false;

        const layerCount = drawing.getNumLayers();
        let changed = false;
        for (let index = 0; index < layerCount; ++index) {
            if (drawing.getLayerName(index) === name) {
                changed = this.canvas.setCurrentLayerPosition(Math.max(0, Math.min(layerCount - 1, index + change)));
                break;
            }
        }

        this.canvas.setCurrentLayer(previousLayer);
        if (changed) this.notifyChanged();
        return changed;
    }

    public setLayerVisibility(name: string, visible: boolean): boolean {
        if (!this.canvas) return false;
        const changed = this.canvas.setLayerVisibility(name, visible);
        if (!changed) return false;
        this.notifyChanged();
        return true;
    }

    public setLayerLocked(name: string, locked: boolean): boolean {
        if (!this.canvas) return false;
        const changed = this.canvas.setLayerLocked(name, locked);
        if (changed) this.notifyChanged();
        return changed;
    }

    public setLayerPrinted(name: string, printed: boolean): boolean {
        const layer = this.canvas?.getCurrentDrawing().getLayerByName(name);
        if (!layer) return false;
        if (printed) layer.removeAttribute(LAYER_PRINT_ATTRIBUTE);
        else layer.setAttribute(LAYER_PRINT_ATTRIBUTE, 'false');
        this.notifyChanged(layer);
        return true;
    }

    public setCurrentLayerMode(mode: string): boolean {
        const drawing = this.canvas?.getCurrentDrawing();
        const layer = drawing?.getLayerByName(drawing.getCurrentLayerName());
        if (!layer) return false;
        if (mode === 'default') layer.removeAttribute('pe-layer-mode');
        else layer.setAttribute('pe-layer-mode', mode);
        this.notifyChanged(layer);
        return true;
    }

    public moveElement(elementId: string, change: number): boolean {
        const element = document.getElementById(elementId) as unknown as SVGGraphicsElement | null;
        if (!element) return false;
        const sibling = change > 0 ? element.nextElementSibling : element.previousElementSibling;
        if (!sibling) return false;
        if (change > 0) sibling.after(element);
        else sibling.before(element);
        this.notifyChanged(element);
        return true;
    }

    public setElementVisibility(elementId: string, visible: boolean): boolean {
        const element = document.getElementById(elementId) as unknown as SVGGraphicsElement | null;
        if (!element) return false;
        element.style.display = visible ? '' : 'none';
        this.notifyChanged(element);
        return true;
    }

    public setElementLocked(elementId: string, locked: boolean): boolean {
        const element = document.getElementById(elementId) as unknown as SVGGraphicsElement | null;
        if (!element || !this.canvas) return false;
        this.canvas.setElementLocked(element, locked);
        this.notifyChanged(element);
        return true;
    }

    public selectElement(layerName: string, elementId: string, addToSelection: boolean, removeFromSelection: boolean): boolean {
        const element = document.getElementById(elementId) as unknown as SVGGraphicsElement | null;
        if (!element || !this.canvas || !this.canvas.setCurrentLayer(layerName, true)) return false;

        if (removeFromSelection) this.canvas.removeFromSelection([element]);
        else if (addToSelection) this.canvas.addToSelection([element]);
        else this.canvas.selectOnly([element]);
        return true;
    }

    public scrollElementIntoView(elementId: string): void {
        document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    public beginDragToggle(mode: string, targetState: boolean, initialKey: string): void {
        this.activeDragToggle = { mode, targetState, appliedKeys: new Set([initialKey]) };
    }

    public applyDragToggle(key: string, mode: string, currentState: boolean): number {
        const state = this.activeDragToggle;
        if (!state || state.mode !== mode || state.appliedKeys.has(key)) return -1;
        state.appliedKeys.add(key);
        return state.targetState === currentState ? -1 : state.targetState ? 1 : 0;
    }

    public endDragToggle(): void {
        this.activeDragToggle = null;
    }
}

window['_zaa_services'] = window['_zaa_services'] || {};
const layersInterop = new LayersInterop();
window['_zaa_services']['layersInterop'] = layersInterop;
window.addEventListener('mouseup', () => layersInterop.endDragToggle(), true);
window.addEventListener('dragend', () => layersInterop.endDragToggle(), true);
window.addEventListener('scroll', () => window.dispatchEvent(new CustomEvent('layers-context-menu-close')), true);
